import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Table, Input, Button, Modal, Menu as AntMenu, Tag } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import './Bidding.scss';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { bidingService } from "../../services/BidingService";
import { useLocation } from 'react-router-dom';
import { useParams } from "react-router-dom";
import Header from "../../components/Header/Header";
import {
    buildCategoryOptionsFromEndpoint,
    createBackendCategoryPathResolver,
    createBackendCategoryResolver,
    filterProductsBySearchAndCategories,
    mergeCategoryOptionsWithProducts,
    resolveStatusTagProps
} from './biddingFilters';
import { useDesktopTableScroll } from './useDesktopTableScroll';
import { createBarcodeCellRenderer } from './renderBarcodeCell';
import { createProductNameCellRenderer } from './renderProductNameCell';
import {
    buildLastWinningPriceMap,
    createLastWinningPriceColumn,
    mergeLastWinningPricesIntoProducts
} from './renderLastWinningPriceCell';
import SupplierMenu from '../../components/SupplierMenu/SupplierMenu'
import { useActiveBiddingGuard } from './useActiveBiddingGuard';
const { Search } = Input;
const RegisteredOffers = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [endTime, setEndTime] = useState(null);
    const [timeLeft, setTimeLeft] = useState('--:--');
    const [editingOfferId, setEditingOfferId] = useState(null);
    const [editedOfferPrice, setEditedOfferPrice] = useState('');
    const location = useLocation();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategories, setSelectedCategories] = useState(['all']);
    const [offerPrices, setOfferPrices] = useState({});
    const [listId, setListId] = useState(null);
    const [biddingCounts, setBiddingCounts] = useState(null);
    const [deleteDialogContext, setDeleteDialogContext] = useState(null);
    const [apiCategoryOptions, setApiCategoryOptions] = useState([]);
    const [lastWinningPriceMap, setLastWinningPriceMap] = useState({});
    const { canLoadBiddingData } = useActiveBiddingGuard(id, navigate);
    const pathname = location.pathname;
    let selectedMenuKey = null;
    if (pathname.startsWith('/produse-ofertare')) {
        selectedMenuKey = 'produse';
    } else if (pathname.startsWith('/produse-ascunse')) {
        selectedMenuKey = 'ascunse';
    } else if (pathname.startsWith('/oferte-inregistrate')) {
        selectedMenuKey = 'oferte';
    }
    const refreshBiddingCounts = useCallback(() => {
        if (!id) return Promise.resolve();
        return bidingService.getBiddingCounts(id)
            .then((res) => {
                setBiddingCounts(res?.data || null);
            })
            .catch((err) => {
                console.warn('Failed to refresh bidding_counts', err);
            });
    }, [id]);

    useEffect(() => {
        if (id && canLoadBiddingData) {
            setLoading(true);
            refreshBiddingCounts();
            bidingService.getBiddingCategories(id)
                .then((categoryRes) => {
                    setApiCategoryOptions(
                        buildCategoryOptionsFromEndpoint(categoryRes?.data, ['registered_offers'])
                    );
                    return bidingService.getRegisteredOffers(id).then((offersRes) => ({
                        categoryData: categoryRes?.data,
                        offersData: offersRes?.data
                    }));
                })
                .then(({ categoryData, offersData }) => {
                    const items = offersData?.bid_items || [];
                    const resolveCategoryLabel = createBackendCategoryResolver(categoryData);
                    const resolveCategoryPath = createBackendCategoryPathResolver(categoryData);
                    setListId(offersData.necessity_list.id);
                    const formattedProducts = items.map(item => ({
                        key: item.id,
                        necessityListItemId: item.id,
                        bidId: item?.bid?.id || item?.current_user_bid?.id || item?.bid_id,
                        name: item.product?.name,
                        barcode: item.product?.barcode,
                        category: resolveCategoryLabel(item),
                        category_path: resolveCategoryPath(item),
                        quantity: item.quantity,
                        unit_measurement:
                            item.unit_measurement ||
                            item.product?.unit_measurement ||
                            item.product?.um ||
                            item.product?.unit ||
                            '',
                        instructions: item.supplementary_info,
                        statusTag: resolveStatusTagProps(item.bid?.status || item.status, "Fara oferta"),
                        hasBid: !!item.bid,
                        offer: item.bid?.price ?? null,
                        originalData: item
                    }));
                    const initialOfferPrices = {};
                    formattedProducts.forEach(product => {
                        if (product.offer !== null) {
                            initialOfferPrices[product.key] = product.offer;
                        }
                    });
                    setProducts(formattedProducts);
                    setOfferPrices(initialOfferPrices);
                })
                .catch((err) => {
                    console.error("Error fetching registered offers/categories:", err);
                    toast.error("A apărut o eroare la încărcarea ofertelor sau categoriilor.");
                })
                .finally(() => setLoading(false));
            bidingService.getList(id)
                .then((res) => {
                    if (res?.data?.id) {
                        setListId(res.data.id);
                    }
                    const biddingEndTime = res?.data?.bidding_end_time;
                    if (biddingEndTime) {
                        setEndTime(new Date(biddingEndTime));
                    }
                })
                .catch((err) => {
                    console.warn('Failed to fetch bidding end time', err);
                });
        }
    }, [id, canLoadBiddingData, refreshBiddingCounts]);
    useEffect(() => {
        if (!id || !canLoadBiddingData) return;

        bidingService.getLastWinningPrices(id)
            .then((res) => {
                setLastWinningPriceMap(buildLastWinningPriceMap(res?.data));
            })
            .catch((err) => {
                console.warn('Failed to fetch last winning prices', err);
            });
    }, [id, canLoadBiddingData]);
    useEffect(() => {
        setProducts((previousProducts) => {
            const nextProducts = mergeLastWinningPricesIntoProducts(previousProducts, lastWinningPriceMap);
            const hasChanges = nextProducts.some((product, index) => (
                product.lastWinningPrice !== previousProducts[index]?.lastWinningPrice
                || product.lastWinningWonAt !== previousProducts[index]?.lastWinningWonAt
            ));

            return hasChanges ? nextProducts : previousProducts;
        });
    }, [products, lastWinningPriceMap]);
    useEffect(() => {
        if (!endTime) {
            setTimeLeft('--:--');
            return;
        }
        const interval = setInterval(() => {
            const now = new Date();
            const difference = endTime - now;
            if (difference <= 0) {
                clearInterval(interval);
                setTimeLeft('00:00');
            } else {
                const minutes = Math.floor(difference / 1000 / 60);
                const seconds = Math.floor((difference / 1000) % 60);
                setTimeLeft(`${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')} minute`);
            }
        }, 1000);
        return () => clearInterval(interval);
    }, [endTime]);
    const pickBiddingCount = (...keys) => {
        const roots = [biddingCounts, biddingCounts?.counts, biddingCounts?.data];
        for (const root of roots) {
            if (!root) continue;
            for (const k of keys) {
                const v = root?.[k];
                if (v === null || v === undefined) continue;
                const parsedValue = Number(v);
                if (Number.isFinite(parsedValue)) return parsedValue;
            }
        }
        return undefined;
    };

    const categoryOptions = useMemo(
        () => mergeCategoryOptionsWithProducts(apiCategoryOptions, products),
        [apiCategoryOptions, products]
    );
    const filteredProducts = useMemo(() => filterProductsBySearchAndCategories(products, searchQuery, selectedCategories), [products, searchQuery, selectedCategories]);
    const { tableContentRef, footerRef, tableScrollConfig } = useDesktopTableScroll([filteredProducts.length]);
    const getBidIdentifiers = (record) => ({
        necessityListId:
            listId ||
            record?.originalData?.necessity_list?.id ||
            record?.originalData?.necessity_list_id ||
            record?.originalData?.bid?.necessity_list_id,
        necessityListItemId:
            record?.necessityListItemId ||
            record?.originalData?.id ||
            record?.originalData?.necessity_list_item_id ||
            record?.key,
        bidId:
            record?.bidId ||
            record?.originalData?.bid?.id ||
            record?.originalData?.current_user_bid?.id ||
            record?.originalData?.bid_id
    });
    const handleEdit = (record) => {
        const { bidId } = getBidIdentifiers(record);
        if (!bidId) {
            toast.error("Oferta nu poate fi editata deoarece lipseste ID-ul bid-ului.");
            return;
        }
        setEditingOfferId(record.key);
        setEditedOfferPrice(String(offerPrices[record.key] ?? record.offer ?? ''));
    };
    const handleCancelEdit = () => {
        setEditingOfferId(null);
        setEditedOfferPrice('');
    };
    const handleEditSubmit = (record) => {
        const { necessityListId, necessityListItemId, bidId } = getBidIdentifiers(record);
        const parsedPrice = Number(editedOfferPrice);
        if (!necessityListId || !necessityListItemId || !bidId) {
            toast.error("Nu am putut identifica oferta pentru actualizare.");
            return;
        }
        if (!Number.isFinite(parsedPrice) || parsedPrice <= 0) {
            toast.error("Introdu un pret valid mai mare decat 0.");
            return;
        }
        bidingService.updateBid(necessityListId, necessityListItemId, bidId, {
            price: parsedPrice,
            comment: record?.originalData?.bid?.comment || ''
        })
            .then(() => {
                setOfferPrices(prev => ({ ...prev, [record.key]: parsedPrice }));
                setProducts(prev => prev.map(product => (
                    product.key === record.key
                        ? {
                            ...product,
                            offer: parsedPrice,
                            originalData: {
                                ...product.originalData,
                                bid: {
                                    ...(product.originalData?.bid || {}),
                                    id: bidId,
                                    price: parsedPrice
                                }
                            }
                        }
                        : product
                )));
                handleCancelEdit();
                toast.success(`Oferta pentru ${record.name} a fost actualizata.`);
            })
            .catch((error) => {
                console.error("Error updating offer:", error);
                toast.error("A aparut o eroare la actualizarea ofertei.");
            });
    };
    const handleDelete = (record) => {
        const { necessityListId, necessityListItemId, bidId } = getBidIdentifiers(record);
        if (!necessityListId || !necessityListItemId || !bidId) {
            toast.error(`Nu am putut identifica oferta pentru stergere (list:${necessityListId || '-'} item:${necessityListItemId || '-'} bid:${bidId || '-'})`);
            return;
        }
        setDeleteDialogContext({
            record,
            necessityListId,
            necessityListItemId,
            bidId
        });
    };
    const handleConfirmDelete = () => {
        if (!deleteDialogContext) return;
        const { record, necessityListId, necessityListItemId, bidId } = deleteDialogContext;
        bidingService.deleteBid(necessityListId, necessityListItemId, bidId)
            .then(() => {
                setProducts(prev => prev.filter(product => product.key !== record.key));
                setOfferPrices(prev => {
                    const next = { ...prev };
                    delete next[record.key];
                    return next;
                });
                toast.success(`Oferta pentru ${record.name} a fost stearsa.`);
                setDeleteDialogContext(null);
                refreshBiddingCounts();
            })
            .catch((error) => {
                console.error("[RegisteredOffers] delete error:", error?.response || error);
                toast.error("A aparut o eroare la stergerea ofertei.");
            });
    };
    const handleSearch = (value) => {
        setSearchQuery(value || '');
    };
    const renderBarcodeCell = useMemo(() => createBarcodeCellRenderer(), []);
    const renderProductNameCell = useMemo(() => createProductNameCellRenderer(), []);
    const columns = [
        {
            title: 'Nume produs',
            dataIndex: 'name',
            key: 'name',
            width: 280,
            className: 'biding__product-name-cell',
            render: renderProductNameCell
        },
        {
            title: 'Cod de bare',
            dataIndex: 'barcode',
            key: 'barcode',
            width: 230,
            render: renderBarcodeCell,
        },
        {
            title: 'Cantitate',
            key: 'quantity',
            width: 130,
            className: 'biding__compact-cell',
            render: (_, record) => {
                const quantity = record.quantity ?? '-';
                const unit = record.unit_measurement ? ` ${record.unit_measurement}` : '';
                return `${quantity}${unit}`;
            },
        },
        createLastWinningPriceColumn(),
        {
            title: (
                <div className="biding__price-header">
                    <span>Oferta preț</span>
                    <small>TVA inclus</small>
                </div>
            ),
            key: 'offerPrice',
            width: 220,
            className: 'biding__compact-cell',
            render: (_, record) => {
                if (editingOfferId === record.key) {
                    return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Input
                                type="number"
                                min={0}
                                value={editedOfferPrice}
                                onChange={(e) => setEditedOfferPrice(e.target.value)}
                                onPressEnter={() => handleEditSubmit(record)}
                                autoFocus
                                style={{ minWidth: 120 }}
                            />
                            <Button type="primary" size="small" onClick={() => handleEditSubmit(record)}>
                                Salvează
                            </Button>
                            <Button size="small" onClick={handleCancelEdit}>
                                Renunță
                            </Button>
                        </div>
                    );
                }
                return (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span>{offerPrices[record.key] ? `${offerPrices[record.key]} RON` : '-'}</span>
                        <Button
                            type="link"
                            icon={<EditOutlined />}
                            onClick={() => handleEdit(record)}
                            style={{ paddingInline: 0 }}
                        >
                            Editează
                        </Button>
                    </div>
                );
            },
        },
        {
            title: 'Status',
            key: 'status',
            width: 140,
            className: 'biding__compact-cell',
            render: (_, record) => (
                <Tag color={record.statusTag?.color || 'default'}>
                    {record.statusTag?.label || 'Fara oferta'}
                </Tag>
            ),
        },
        {
            title: 'Acțiuni',
            key: 'actions',
            width: 120,
            className: 'biding__compact-cell',
            render: (_, record) => {
                return (
                    <Button
                        type="link"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => handleDelete(record)}
                    >
                        Retrage ofertă
                    </Button>
                );
            },
        },
    ];
    return (
        <div className='standard products list biding'>
            <Header />
            <div className='standard__mobile-header'>
                <img src='/images/logo.png' alt='logo' className='standard__only-mobile standard__mobile-logo' />
            </div>
            <div className='standard__container list '>
                <SupplierMenu
                    categoryOptions={categoryOptions}
                    selectedCategories={selectedCategories}
                    onCategoriesChange={setSelectedCategories}
                />
                <div className='standard__wrapper biding__wrapper'>
                    <div className='biding__content'>
                        <div className='biding__header'>
                            <div className='biding__header-main'>
                                <h2 className='standard__page-title'>Oferte înregistrate</h2>
                                <h4 className='standard__page-info'>
                                    Până la expirarea timpului poți actualiza ofertele înregistrate. După încheiere, rezultatele finale sunt comunicate prin e-mail sau WhatsApp.
                                </h4>
                            </div>
                            <div className='biding__header-side'>
                                <div className='biding__top-area'>
                                    <div className='list__search-box biding__search-box'>
                                        <Search
                                            className="list__search-input"
                                            placeholder="Caută produs..."
                                            onChange={(e) => handleSearch(e.target.value)}
                                            value={searchQuery}
                                            enterButton
                                            allowClear
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <AntMenu
                            mode="horizontal"
                            selectedKeys={[selectedMenuKey]}
                            onClick={(e) => {
                                switch (e.key) {
                                    case 'produse':
                                        navigate(`/produse-ofertare/${id}`);
                                        break;
                                    case 'ascunse':
                                        navigate(`/produse-ascunse/${id}`);
                                        break;
                                    case 'oferte':
                                        navigate(`/oferte-inregistrate/${id}`);
                                        break;
                                    default:
                                        break;
                                }
                            }}
                        >
                            <AntMenu.Item key="produse">
                                Produse pentru ofertare{typeof pickBiddingCount('remaining_items', 'remaining_items_count', 'biddable', 'biddable_count', 'for_bidding', 'for_bidding_count', 'offering', 'offering_count', 'products', 'products_count') === 'number' ? ` (${pickBiddingCount('remaining_items', 'remaining_items_count', 'biddable', 'biddable_count', 'for_bidding', 'for_bidding_count', 'offering', 'offering_count', 'products', 'products_count')})` : ''}
                            </AntMenu.Item>
                            <AntMenu.Item key="ascunse">
                                Produse ascunse{typeof pickBiddingCount('hidden', 'hidden_count', 'hidden_items', 'hidden_items_count', 'hidden_products', 'hidden_products_count') === 'number' ? ` (${pickBiddingCount('hidden', 'hidden_count', 'hidden_items', 'hidden_items_count', 'hidden_products', 'hidden_products_count')})` : ''}
                            </AntMenu.Item>
                            <AntMenu.Item key="oferte">
                                Oferte inregistrate{typeof pickBiddingCount('offered_items', 'offered_items_count', 'bid_items', 'bid_items_count', 'bids', 'bids_count', 'offered', 'offered_count', 'registered', 'registered_count') === 'number' ? ` (${pickBiddingCount('offered_items', 'offered_items_count', 'bid_items', 'bid_items_count', 'bids', 'bids_count', 'offered', 'offered_count', 'registered', 'registered_count')})` : ''}
                            </AntMenu.Item>
                        </AntMenu>
                        <div className="list__table-wrapper">
                            <div className='list__table-content' ref={tableContentRef}>
                                <Table
                                    dataSource={filteredProducts}
                                    columns={columns}
                                    pagination={false}
                                    rowKey="key"
                                    loading={loading}
                                    scroll={{ ...(tableScrollConfig || {}), x: 1780 }}
                                    tableLayout="fixed"
                                />
                            </div>
                            <div className='biding__bottom-wrapper' ref={footerRef}>
                                <div className='biding__time-box'>
                                    <div className='biding__time'>
                                        <h5>Timp ramas pentru ofertare: </h5>
                                        <h4>{timeLeft}</h4>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Modal
                open={!!deleteDialogContext}
                title="Retrage oferta?"
                okText="Retrage oferta"
                okType="danger"
                cancelText="Renunta"
                onCancel={() => setDeleteDialogContext(null)}
                onOk={handleConfirmDelete}
            >
                Oferta pentru {deleteDialogContext?.record?.name} va fi retrasa.
            </Modal>
        </div>
    );
};
export default RegisteredOffers;